import type {ApplicationKey} from '@/configs/proposition/propositionConfig.types';
import type {EnvironmentTypes} from '@myparcel-frontend/service-utils';
import type {ValueOf} from '@myparcel-frontend/types';
import {useFetch} from '@vueuse/core';
import {defineStore} from 'pinia';
import {computed, reactive, ref} from 'vue';
import {getApplicationKey} from '@/utils/host';

export type GlobalConfig = {
  [key: string]: unknown;
  auth0Domain: Record<string, string>;
  auth0ClientId: Record<string, string>;
  auth0Audience: string;
  auth0Scope: string;
  apiUrl: string;
  assetsUrls: Record<string, string>;
  devMode: boolean;
  useMockingApiClients: boolean;
  hotjar: Record<string, string>;
  frontofficeUrl: Record<string, string>;
  hereMapsApiKey: string;
  environment: EnvironmentTypes;
  newRelic?: {
    accountId: string;
    licenseKey: string;
    beacon: string;
    applicationId: string;
    appUrl: string;
    releaseName: string;
  };
};

export type Environment = {
  auth0: {
    domain: string;
    clientId: string;
    audience: string;
    scope: string;
  };
  apiUrl: string;
  assetsUrl: string;
  devMode: boolean;
  useMockingApiClients: boolean;
  hotjar: string;
  frontofficeUrl: string;
  hereMapsApiKey: string;
  environment: EnvironmentTypes;
  newRelic: {
    accountId: string;
    licenseKey: string;
    beacon: string;
    applicationId: string;
    appUrl: string;
    releaseName: string;
  };
};

export const useEnvironmentStore = defineStore('environmentStore', () => {
  const globalConfig = ref<GlobalConfig>();
  const environment = reactive<Environment>({
    auth0: {
      domain: '',
      clientId: '',
      audience: '',
      scope: '',
    },
    apiUrl: '',
    assetsUrl: '',
    devMode: false,
    useMockingApiClients: false,
    hotjar: '',
    frontofficeUrl: '',
    hereMapsApiKey: '',
    environment: 'production',
    newRelic: {
      accountId: '',
      licenseKey: '',
      beacon: '',
      applicationId: '',
      appUrl: '',
      releaseName: '',
    },
  });

  const setViteEnv = (applicationKey: ValueOf<typeof ApplicationKey>) => {
    environment.auth0.audience = import.meta.env.VITE_AUTH0_AUDIENCE ?? '';
    environment.auth0.scope = import.meta.env.VITE_AUTH0_SCOPE ?? '';
    environment.apiUrl = import.meta.env.VITE_API_URL ?? '';
    environment.devMode = import.meta.env.VITE_DEV_MODE === 'true';
    environment.useMockingApiClients = import.meta.env.VITE_USE_MOCKING_API_CLIENTS === 'true';
    environment.hotjar = import.meta.env.VITE_HOTJAR_ID ?? '';
    environment.frontofficeUrl = import.meta.env.VITE_FRONTOFFICE_URL ?? '';
    environment.assetsUrl = import.meta.env.VITE_ASSETS_URL ?? '';
    environment.hereMapsApiKey = import.meta.env.VITE_HEREMAPS_API_KEY ?? '';

    if (typeof import.meta.env.VITE_ENVIRONMENT === 'string') {
      environment.environment = import.meta.env.VITE_ENVIRONMENT.toLowerCase();
    }

    switch (applicationKey) {
      case 'myparcel-nederland':
        environment.assetsUrl = import.meta.env.VITE_MYPARCEL_NEDERLAND_ASSETS_URL ?? '';
        environment.auth0.clientId = import.meta.env.VITE_MYPARCEL_NEDERLAND_AUTH0_CLIENT_ID ?? '';
        environment.auth0.domain = import.meta.env.VITE_MYPARCEL_NEDERLAND_AUTH0_DOMAIN ?? '';
        break;
      case 'myparcel-belgie':
        environment.assetsUrl = import.meta.env.VITE_MYPARCEL_BELGIE_ASSETS_URL ?? '';
        environment.auth0.clientId = import.meta.env.VITE_MYPARCEL_BELGIE_AUTH0_CLIENT_ID ?? '';
        environment.auth0.domain = import.meta.env.VITE_MYPARCEL_BELGIE_AUTH0_DOMAIN ?? '';
        break;
      case 'myparcel-com':
        environment.assetsUrl = import.meta.env.VITE_MYPARCEL_COM_ASSETS_URL ?? '';
        environment.auth0.clientId = import.meta.env.VITE_MYPARCEL_COM_AUTH0_CLIENT_ID ?? '';
        environment.auth0.domain = import.meta.env.VITE_MYPARCEL_COM_AUTH0_DOMAIN ?? '';
        break;
    }
  };

  const setConfigEnv = (config: GlobalConfig, applicationKey: ValueOf<typeof ApplicationKey>) => {
    environment.auth0.domain = config.auth0Domain?.[applicationKey] ?? '';
    environment.auth0.audience = config.auth0Audience ?? '';
    environment.auth0.scope = config.auth0Scope ?? '';
    environment.auth0.clientId = config.auth0ClientId?.[applicationKey] ?? '';

    environment.apiUrl = config.apiUrl ?? '';
    environment.assetsUrl = config.assetsUrls?.[applicationKey] ?? '';
    environment.devMode = config.devMode ?? false;
    environment.useMockingApiClients = config.useMockingApiClients ?? false;
    environment.hotjar = config.hotjar?.[applicationKey] ?? '';
    environment.frontofficeUrl = config.frontofficeUrl?.[applicationKey] ?? '';
    environment.hereMapsApiKey = config.hereMapsApiKey ?? '';
    environment.environment = config.environment ? config.environment.toLowerCase() : 'production';

    if (config.newRelic) {
      environment.newRelic = {
        accountId: config.newRelic.accountId,
        licenseKey: config.newRelic.licenseKey,
        beacon: config.newRelic.beacon,
        applicationId: config.newRelic.applicationId,
        appUrl: config.newRelic.appUrl,
        releaseName: config.newRelic.releaseName,
      };
    }
  };

  const loadGlobalConfig = async () => {
    const {error, data} = await useFetch('/staticv2/env/env.json').json<GlobalConfig>();

    if (error.value || !data.value) {
      console.warn('Could not load global config, falling back to Vite env variables', error.value);
      return;
    }

    globalConfig.value = data.value;
  };

  const setEnv = () => {
    const applicationKey = getApplicationKey();
    if (globalConfig.value) {
      setConfigEnv(globalConfig.value, applicationKey);
      return;
    }

    setViteEnv(applicationKey);
  };

  const envIsSet = computed(() => {
    return (
      !!environment.auth0.domain.length &&
      !!environment.auth0.clientId.length &&
      !!environment.auth0.audience.length &&
      !!environment.auth0.scope.length &&
      !!environment.apiUrl.length
    );
  });

  return {
    environment,
    globalConfig,
    envIsSet,
    setEnv,
    loadGlobalConfig,
  };
});
